import { Button } from '@/components/Button'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { observer } from 'mobx-react-lite'
import type { ReactNode } from 'react'
import type { BookkeepingPlatformSelectionDialogViewModel } from './BookkeepingPlatformSelectionDialogViewModel'
import {
  CardItemContent,
  CardList,
  CardListItem,
  CardTitle,
} from '@/components/Card'
import {
  QuickBooksConnectionsDialogContainer,
  XeroConnectionsDialogContainer,
} from '../BookkeepingPlatformConnectionsDialog'
import { formatBookkeepingPlatform } from '@/features/bookkeeping/platform-connections/bookkeeping-platform-connection-formatters'
import { BookkeepingOtherConnectionDialogContainer } from '../BookkeepingOtherConnectionDialog'

import { allBookkeepingPlatforms } from '@/features/bookkeeping/platform-connections/bookkeeping-platform'

interface BookkeepingPlatformSelectionDialogContainerProps {
  vm: BookkeepingPlatformSelectionDialogViewModel
}

/**
 * The BookkeepingPlatformSelectionDialogContainer
 */
export const BookkeepingPlatformSelectionDialogContainer = observer(
  ({ vm }: BookkeepingPlatformSelectionDialogContainerProps) => {
    return (
      <BookkeepingPlatformSelectionDialog
        isOpen={vm.modal.showing}
        onClose={vm.close}
        testId="bookkeeping-platform-selection-dialog"
      >
        <PlatformList>
          {allBookkeepingPlatforms.map((platform, index) => (
            <PlatformListItem
              key={index}
              title={formatBookkeepingPlatform(platform)}
              onSelect={() => vm.selectPlatform(platform)}
              testId={`platform-button-${platform}`}
            />
          ))}
        </PlatformList>

        <XeroConnectionsDialogContainer vm={vm.xeroConnectionsDialogVm} />
        <QuickBooksConnectionsDialogContainer
          vm={vm.quickBooksConnectionsDialogVm}
        />
        <BookkeepingOtherConnectionDialogContainer
          vm={vm.bookkeepingOtherConnectionsDialogVm}
        />
      </BookkeepingPlatformSelectionDialog>
    )
  },
)

interface BookkeepingPlatformSelectionDialogProps {
  children?: ReactNode
  isOpen: boolean
  isPending?: boolean
  isDisabled?: boolean
  onClose?: () => unknown
  testId?: string
}

/**
 * The BookkeepingPlatformSelectionDialog
 */
export function BookkeepingPlatformSelectionDialog({
  children,
  isOpen,
  isPending,
  onClose,
  testId,
}: BookkeepingPlatformSelectionDialogProps) {
  return (
    <ModalDialog
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isPending}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>Share access to your books</ModalDialogTitle>
        <ModalDialogDescription>
          Connect your bookkeeping account to get started.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>{children}</ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Close"
            variant="outline"
            isDisabled={isPending}
            onPress={onClose}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface PlatformListProps {
  children?: ReactNode
}

/**
 * The PlatformList
 */
export function PlatformList({ children }: PlatformListProps) {
  return <CardList aria-label="Platforms">{children}</CardList>
}

interface PlatformListItemProps {
  title: string
  onSelect?: () => void
  testId?: string
}

/**
 * PlatformListItem
 */
export function PlatformListItem({
  title,
  onSelect,
  testId,
}: PlatformListItemProps) {
  return (
    <CardListItem onAction={onSelect} textValue={title} testId={testId}>
      <CardItemContent isInteractive>
        <CardTitle>{title}</CardTitle>
      </CardItemContent>
    </CardListItem>
  )
}
